import { useCallback, useMemo } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { MonitoringHook } from '../types'

export interface DatadogConfig {
  applicationId: string
  clientToken: string
  site: string
  service: string
  env: string | undefined
  allowedTracingUrls: string[]
}

/**
 * useDatadogMonitoring
 *
 * Hook for Datadog RUM monitoring (cloud deployments).
 * Provides monitoring and observability using Datadog Real User Monitoring.
 *
 * Note: Datadog is initialized in services/initialization.ts before React renders.
 */
export default function useDatadogMonitoring(): MonitoringHook {
  const addAction = useCallback((name: string, params?: Record<string, unknown>) => {
    datadogRum.addAction(name, params)
  }, [])

  const logError = useCallback((error: Error, context?: Record<string, unknown>) => {
    datadogRum.addError(error, context)
  }, [])

  const setUser = useCallback((user: { email: string; [key: string]: unknown }) => {
    datadogRum.setUser(user)
  }, [])

  const setUserProperty = useCallback((key: string, value: unknown) => {
    datadogRum.setUserProperty(key, value)
  }, [])

  return useMemo(
    () => ({
      addAction,
      logError,
      setUser,
      setUserProperty,
    }),
    [addAction, logError, setUser, setUserProperty]
  )
}
